import axios, { isCancel } from 'axios';
import { useState, useEffect } from 'react';
import { getSearchResults } from '@pm2/api';
import { pm2ConfigStore } from '@pm2/store';
import { useSelf } from '@utils/react';

// eslint-disable-next-line import-x/no-named-as-default-member
const CancelToken = axios.CancelToken;
const DEBOUNCE_DURATION = 350;
/**
 * Handles fetching search results using the PM2 API for the current query
 * string using debounced calls to avoid searches while the user is typing.
 *
 * Returns the current search-results, the query used to fetch these results
 * and whether or not new results are being fetched.
 */
function useSearch({
  query,
  debounceDuration
}) {
  const previewMode = pm2ConfigStore.useSelector(state => state.previewMode);
  const portalId = pm2ConfigStore.useSelector(state => state.config.id);
  const [isSearching, setSearching] = useState(false);
  const [state, setState] = useState({
    searchResults: [],
    currentQuery: query
  });
  const self = useSelf({});
  useEffect(() => {
    // Ensure previous query requests are cancelled on new input
    self.cancellationToken?.cancel();
    self.cancellationToken = undefined;
    window.clearTimeout(self.debounceTimeout);
    self.debounceTimeout = undefined;
    setSearching(false);

    // Don't bother executing a query if the user has typed less than two
    // characters
    if (query.length <= 1) {
      setState({
        searchResults: [],
        currentQuery: ""
      });
      setSearching(false);
      return;
    }
    const performSearch = async () => {
      // Abort previous requests if such exists, and prepare a new
      // cancellation- token
      self.cancellationToken?.cancel();
      self.cancellationToken = CancelToken.source();
      try {
        const promisedResults = getSearchResults(portalId, query, {
          cancelToken: self.cancellationToken,
          previewMode
        });
        if (!(promisedResults instanceof Promise)) {
          // if promise were instantly available, then simpl apply them
          // right away
          setState({
            currentQuery: query,
            searchResults: promisedResults
          });
          setSearching(false);
          return;
        }

        // .. otherwise register that we're searching, and update state as
        // soon as it becomes available
        setSearching(true);
        setState({
          currentQuery: query,
          searchResults: await promisedResults
        });
        setSearching(false);
        self.cancellationToken = undefined;
      } catch (err) {
        // In case an actual error occurs during search, then notify the user
        if (!isCancel(err)) {
          setSearching(false);
          window.alert("Noget gik galt da vi forsøgte at hente resultaterne af din søgning. Prøv igen senere.");
        }
      }
    };

    // Start a debounced search in order to avoid unnecessary requests when
    // users are actively typing search queries
    self.debounceTimeout = window.setTimeout(() => {
      performSearch();
    }, debounceDuration ?? DEBOUNCE_DURATION);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [query]);
  return [isSearching, state];
}

export { useSearch };
//# sourceMappingURL=hook.search.js.map
